import { MAX_TICKET_ATTACHMENT_BYTES, MAX_TICKET_ATTACHMENTS } from '@hearth/shared';
import { useRef, useState } from 'react';
import { type VoiceTextareaHandle, type VoiceTextareaProps, useToast } from '../../components/ui';

/**
 * State for one VoiceTextarea message: the text, whether any of it was dictated (sent as `dictated`), and the files
 * attached to it. Files the server would refuse are left out with a message instead of failing the whole send.
 */
export function useMessageDraft() {
  const toast = useToast();
  const ref = useRef<VoiceTextareaHandle>(null);
  const [text, setText] = useState('');
  const [dictated, setDictated] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  const onChange: VoiceTextareaProps['onChange'] = (value, source) => {
    setText(value);
    if (source === 'voice') setDictated(true);
  };

  const addFiles = (added: Iterable<File>) => {
    const next = [...files];
    for (const file of added) {
      if (file.size > MAX_TICKET_ATTACHMENT_BYTES) toast.error(`“${file.name}” is larger than ${MAX_TICKET_ATTACHMENT_BYTES / 1024 / 1024} MB`);
      else if (file.size === 0) toast.error(`“${file.name}” is empty`);
      else if (next.length >= MAX_TICKET_ATTACHMENTS) {
        toast.error(`Attach at most ${MAX_TICKET_ATTACHMENTS} files to one message`);
        break;
      } else next.push(file);
    }
    setFiles(next);
  };

  return {
    boxProps: { ref, value: text, onChange },
    empty: text.trim() === '',
    files,
    addFiles,
    removeFile: (index: number) => setFiles((list) => list.filter((_, i) => i !== index)),
    /** Stops voice typing (keeping the words still being recognised) and returns what to send. */
    take: (): { body: string; dictated: boolean; files: File[] } => {
      const body = ref.current?.stop() ?? text;
      // Stopping may have just added dictated words that aren't in state yet.
      return { body, dictated: dictated || body !== text, files };
    },
    reset: () => {
      setText('');
      setDictated(false);
      setFiles([]);
    },
  };
}
